import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  TrendingUp,
  RefreshCw,
  TrendingDown,
  DollarSign,
  Activity,
  ArrowUpRight,
  Database,
  Calendar,
  Cpu
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  CartesianGrid
} from "recharts";
import { useNexoraXLedger } from "../lib/NexoraXLedgerManager";

interface TaxEntry {
  id: string;
  date: string;
  grossHarvest: number;
  NexoraXLevy: number;
  charity1: number;
  charity2: number;
  charity3: number;
  lifetimeTax: number;
  netSettlement: number;
  SecurePaymentBankRoute: string;
  gdriveMirrorStatus: string;
}

export function RevenueTicker() {
  const { metrics, loading: contextLoading, refreshLedger: triggerCentralRefresh } = useNexoraXLedger();
  const [ledger, setLedger] = useState<TaxEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [isLive, setIsLive] = useState<boolean>(true);

  const fetchLedger = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/tax/ledger");
      if (res.ok) {
        const data = await res.json();
        // reverse to make chronological chart order (oldest to newest)
        const entries = data.ledger || [];
        setLedger([...entries].reverse());
        setLastUpdated(new Date());
      }
    } catch (err) {
      console.error("Failed to sync tax ledger for Revenue Chart ticker:", err);
    } finally {
      setLoading(false);
    }
  };

  // Poll for live-updating trend metrics
  useEffect(() => {
    fetchLedger();
    
    let interval: NodeJS.Timeout | null = null;
    if (isLive) {
      interval = setInterval(() => {
        fetchLedger();
      }, 4000); // 4 seconds live sync frequency
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isLive]);

  // Compute stats based on ledger
  const totalGross = ledger.reduce((acc, curr) => acc + curr.grossHarvest, 0);
  const averageHarvest = ledger.length > 0 ? totalGross / ledger.length : 0;
  const recentHarvest = ledger.length > 0 ? ledger[ledger.length - 1].grossHarvest : 0;
  
  // Calculate percentage delta from previous harvest to the latest
  let deltaPct = 0;
  if (ledger.length > 1) {
    const prev = ledger[ledger.length - 2].grossHarvest;
    if (prev > 0) {
      deltaPct = ((recentHarvest - prev) / prev) * 100;
    }
  }

  // Format the chart data to be safe for Recharts representation
  const chartData = ledger.map((entry) => ({
    name: entry.id.replace("TX-2026-", "#"),
    date: entry.date,
    harvest: entry.grossHarvest,
    net: entry.netSettlement,
    levy: entry.NexoraXLevy,
    tax: entry.lifetimeTax,
    raw: entry
  }));

  // Custom tool-tip for professional visual design alignment
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-[#050a18] border border-cyan-500/30 p-4 rounded-xl shadow-2xl space-y-2 text-left font-mono">
          <div className="flex justify-between items-center border-b border-white/10 pb-1.5 gap-6">
            <span className="text-[10px] text-cyan-400 font-bold uppercase tracking-wider">INDEX ID: {data.raw.id}</span>
            <span className="text-[9px] text-slate-500">{data.date}</span>
          </div>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between gap-4">
              <span className="text-slate-400">GROSS HARVEST:</span>
              <span className="text-white font-bold">£{data.harvest.toLocaleString()}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-amber-400">3% Community Allocation (1%):</span>
              <span className="text-amber-300 font-bold">£{data.levy.toLocaleString()}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-purple-400">10% Maintenance & Hosting Fee (20%):</span>
              <span className="text-purple-300 font-bold">£{data.tax.toLocaleString()}</span>
            </div>
            <div className="flex justify-between gap-4 pt-1 border-t border-white/5 font-extrabold text-emerald-400">
              <span>NET finance SecurePayment:</span>
              <span>£{data.net.toLocaleString()}</span>
            </div>
          </div>
          <div className="text-[8px] text-slate-500 uppercase leading-[1.3] pt-1">
            Replicated instantly to NexoraX Resource Library via CloudSync-02.
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Metric Tickers Header Cards */}
        <div className="lg:col-span-12 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-[#050914] border border-cyan-900/40 rounded-xl p-5 shadow-xl relative overflow-hidden flex flex-col justify-between text-left">
            <div>
              <span className="text-[9px] font-mono text-cyan-400 font-bold uppercase tracking-[0.25em] block mb-1">
                TOTAL GROSS HARVEST
              </span>
              <span className="text-2xl font-mono font-black text-white block">
                £{metrics.totalGrossHarvest.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="flex items-center gap-1.5 mt-3 text-[10px] font-mono">
              {deltaPct >= 0 ? (
                <span className="text-emerald-400 flex items-center gap-0.5 font-bold">
                  <ArrowUpRight className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  +{deltaPct.toFixed(2)}% DELTA
                </span>
              ) : (
                <span className="text-rose-400 flex items-center gap-0.5 font-bold">
                  <TrendingDown className="w-3.5 h-3.5 text-rose-400 shrink-0" />
                  {deltaPct.toFixed(2)}% DELTA
                </span>
              )}
              <span className="text-slate-500 uppercase tracking-wider text-[9px]">vs previous sweep</span>
            </div>
          </div>

          <div className="bg-[#050914] border border-cyan-900/40 rounded-xl p-5 shadow-xl relative overflow-hidden flex flex-col justify-between text-left">
            <div>
              <span className="text-[9px] font-mono text-amber-500 font-bold uppercase tracking-[0.25em] block mb-1">
                LEVY CONVEYANCE POOL
              </span>
              <span className="text-2xl font-mono font-black text-amber-400 block">
                £{metrics.NexoraXLevy.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="text-[9px] text-slate-500 font-mono uppercase tracking-wider mt-3">
              1% automatic split to charity foundations
            </div>
          </div>

          <div className="bg-[#050914] border border-cyan-900/40 rounded-xl p-5 shadow-xl relative overflow-hidden flex flex-col justify-between text-left">
            <div>
              <span className="text-[9px] font-mono text-purple-400 font-bold uppercase tracking-[0.25em] block mb-1">
                10% Maintenance & Hosting Fee EXCISED
              </span>
              <span className="text-2xl font-mono font-black text-purple-300 block">
                £{metrics.taxReserve.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="text-[9px] text-slate-500 font-mono uppercase tracking-wider mt-3">
              20% Absolute 10% Maintenance & Hosting Fee Deduction
            </div>
          </div>

          <div className="bg-[#050914] border border-emerald-900/40 rounded-xl p-5 shadow-xl relative overflow-hidden flex flex-col justify-between text-left">
            <div>
              <span className="text-[9px] font-sans text-emerald-400 font-bold uppercase tracking-[0.25em] block mb-1">
                finance RESERVE SETTLEMENTS
              </span>
              <span className="text-2xl font-mono font-black text-emerald-300 block">
                £{metrics.netSettlement.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="text-[9px] text-emerald-500/80 font-mono uppercase tracking-wider mt-3">
              SecurePayment Sort 04-**-** Acc ******15
            </div>
          </div>
        </div>

        {/* Dynamic Real-time Bar Chart Visualizer Column */}
        <div className="lg:col-span-8 bg-[#050914]/90 border border-slate-800 rounded-2xl p-6 shadow-2xl flex flex-col space-y-4 text-left">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-850 border-slate-800/80 pb-4">
            <div>
              <h3 className="text-xs font-bold text-cyan-400 font-mono uppercase tracking-widest flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-cyan-400" /> DAILY GROSS REVENUE TICKER HARVEST TRENDS
              </h3>
              <p className="text-[9px] font-mono text-slate-500 uppercase mt-1">
                Live chronological ledger chart tracking the active global transaction matrices
              </p>
            </div>

            <div className="flex items-center gap-3 font-mono text-[9px]">
              <button
                onClick={() => setIsLive(!isLive)}
                className={`px-3 py-1.5 rounded-lg border transition-all flex items-center gap-1.5 uppercase font-bold text-[8px] cursor-pointer ${
                  isLive
                    ? "bg-cyan-950/30 border-cyan-500/30 text-cyan-400"
                    : "bg-black border-slate-800 text-slate-500 hover:border-slate-700"
                }`}
              >
                <div className={`w-1.5 h-1.5 rounded-full ${isLive ? "bg-cyan-400 animate-pulse" : "bg-slate-500"}`} />
                {isLive ? "LIVE SYNC ON (4S)" : "LIVE SYNC OFF"}
              </button>

              <button
                onClick={fetchLedger}
                disabled={loading}
                className="px-3 py-1.5 bg-black hover:bg-slate-950 text-slate-400 hover:text-white border border-slate-800 hover:border-slate-705 hover:border-slate-700 rounded-lg transition-all cursor-pointer flex items-center gap-1 uppercase font-bold text-[8px]"
              >
                <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin text-cyan-400" : ""}`} />
                FORCE SYNC
              </button>
            </div>
          </div>

          <div className="h-[280px] w-full pt-2">
            {chartData.length === 0 ? (
              <div className="w-full h-full flex flex-col items-center justify-center font-mono text-slate-500 text-xs text-center border border-dashed border-slate-800 rounded-xl space-y-2">
                <Activity className="w-8 h-8 animate-pulse text-slate-600" />
                <span>Gathering trans-meridian ledger parameters...</span>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                <BarChart
                  data={chartData}
                  margin={{ top: 10, right: 10, left: -25, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#0f172a"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    stroke="#475569"
                    fontSize={9}
                    fontFamily="JetBrains Mono, monospace"
                    tickLine={false}
                    axisLine={false}
                    dy={8}
                  />
                  <YAxis
                    stroke="#475569"
                    fontSize={9}
                    fontFamily="JetBrains Mono, monospace"
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(tick) => `£${tick >= 1000000 ? (tick / 1000000).toFixed(1) + 'M' : (tick / 1000).toFixed(0) + 'K'}`}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "rgba(6, 182, 212, 0.04)" }}
                  />
                  <ReferenceLine
                    y={averageHarvest}
                    stroke="#e2e8f0"
                    strokeDasharray="3 3"
                    strokeWidth={0.8}
                    opacity={0.3}
                    label={{
                      value: `AVG HVS: £${Math.round(averageHarvest).toLocaleString()}`,
                      position: 'top',
                      fill: '#94a3b8',
                      fontSize: 8,
                      fontFamily: 'JetBrains Mono, monospace'
                    }}
                  />
                  <Bar
                    dataKey="harvest"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={36}
                  >
                    {chartData.map((entry, index) => {
                      const isLatest = index === chartData.length - 1;
                      return (
                        <Cell
                          key={`cell-${index}`}
                          fill={isLatest ? "url(#latestBarGrad)" : "url(#normBarGrad)"}
                          stroke={isLatest ? "#34d399" : "#0891b2"}
                          strokeWidth={0.7}
                        />
                      );
                    })}
                  </Bar>
                  {/* Define Premium Gradients */}
                  <defs>
                    <linearGradient id="normBarGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.85} />
                      <stop offset="100%" stopColor="#0891b2" stopOpacity={0.15} />
                    </linearGradient>
                    <linearGradient id="latestBarGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#059669" stopOpacity={0.2} />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="flex justify-between items-center text-[8px] font-mono text-slate-500 uppercase border-t border-slate-800/60 pt-3">
            <span className="flex items-center gap-1 text-[8px]">
              <Calendar className="w-3 h-3 text-cyan-400" />
              LAST LEDGER RE-FETCH: {lastUpdated.toLocaleTimeString()}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping"></span>
              GREEN BARS CHRONICLE THE ACTIVE SWEEP ENTRY
            </span>
          </div>
        </div>

        {/* Audit Meta-Grid Metrics Ledger Node Column */}
        <div className="lg:col-span-4 bg-[#050914] border border-slate-800 rounded-2xl p-6 shadow-2xl flex flex-col justify-between">
          <div className="space-y-4 text-left">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <h3 className="text-xs font-bold text-amber-500 font-mono uppercase tracking-widest flex items-center gap-1.5">
                <Cpu className="w-3.5 h-3.5" /> SECURE AUDIT FLUX
              </h3>
              <span className="text-[8px] font-mono text-indigo-400 uppercase tracking-widest font-bold">Node-07 Gavel</span>
            </div>

            <div className="space-y-3 font-mono text-[10px] text-slate-300">
              <div className="flex justify-between items-center bg-black/40 border border-slate-900 px-3 py-2 rounded-lg">
                <span className="text-[9px] text-slate-500 font-bold uppercase">GLOBAL REVENUE MATRIX:</span>
                <span className="text-white font-black tracking-wider">£{totalGross.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>

              <div className="flex justify-between items-center bg-black/40 border border-slate-900 px-3 py-2 rounded-lg">
                <span className="text-[9px] text-slate-500 font-bold uppercase">AVERAGE LEVEL VECTORS:</span>
                <span className="text-cyan-400 font-black tracking-wider">£{averageHarvest.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>

              <div className="flex justify-between items-center bg-black/40 border border-slate-900 px-3 py-2 rounded-lg">
                <span className="text-[9px] text-slate-500 font-bold uppercase">SWEEP ENTRIES CAPTURED:</span>
                <span className="text-amber-500 font-black tracking-wider">{ledger.length} ACTIVE BATCHES</span>
              </div>

              <div className="flex justify-between items-center bg-black/40 border border-slate-900 px-3 py-2 rounded-lg">
                <span className="text-[9px] text-slate-500 font-bold uppercase">CloudSync-02 MIRROR:</span>
                <span className="text-emerald-400 font-bold">STABLE RECOVERABLE</span>
              </div>
            </div>

            <div className="p-3 bg-indigo-950/20 border border-indigo-500/15 rounded-xl text-[9px] border-dashed leading-normal text-slate-400 uppercase">
              ALL SYSTEM VECTORS AND TRANS-REVENUE CLEARINGS COMPLY ACCURATELY WITH NATIONAL UK LAWS & STANDARD OFFICE OF THE DIRECTOR STATUTORY GUIDELINES.
            </div>
          </div>

          <div className="mt-8 border-t border-slate-800 pt-4 text-center">
            <span className="text-[9px] font-mono text-slate-500 uppercase tracking-widest flex items-center justify-center gap-1">
              <Database className="w-3 h-3 text-cyan-400" />
              SYNC STATE: SECURE MEMORY ENGINE
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
